import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { GripVertical } from "lucide-react";
import { api } from "../../lib/api";
import { categoryOf } from "../../lib/category";

const LABELS = {
  iphone: { title: "Featured iPhones — homepage order", blurb: 'Drag to set the order featured iPhones appear in "Popular right now" on the homepage.' },
  ipad: { title: "Featured iPads — homepage order", blurb: 'Drag to set the order featured iPads appear in the "Featured iPads" row on the homepage.' },
  macbook: { title: "Featured MacBooks — homepage order", blurb: 'Drag to set the order featured MacBooks appear in the "Featured MacBooks" row on the homepage.' },
};

export default function FeaturedReorder({ products, onSaved, category = "iphone" }) {
  const labels = LABELS[category] || LABELS.iphone;
  const featured = useMemo(
    () =>
      products
        .filter((p) => p.featured && categoryOf(p) === category)
        .sort((a, b) => {
          const ao = a.featured_order ?? 1e9;
          const bo = b.featured_order ?? 1e9;
          return ao - bo;
        }),
    [products, category]
  );

  const [items, setItems] = useState(featured);
  const [dragIdx, setDragIdx] = useState(null);
  const [saving, setSaving] = useState(false);

  // Re-sync local order whenever the featured set/order changes upstream.
  const signature = featured.map((p) => `${p.id}:${p.featured_order}`).join("|");
  useEffect(() => { setItems(featured); }, [signature]); // eslint-disable-line react-hooks/exhaustive-deps

  const persist = async (ordered) => {
    setSaving(true);
    try {
      await api.patch("/admin/products/featured-order", { order: ordered.map((p) => p.id) });
      toast.success("Homepage order saved");
      onSaved?.();
    } catch {
      toast.error("Couldn't save the new order");
      setItems(featured);
    } finally {
      setSaving(false);
    }
  };

  const onDrop = (dropIdx) => {
    if (dragIdx === null || dragIdx === dropIdx) { setDragIdx(null); return; }
    const next = [...items];
    const [moved] = next.splice(dragIdx, 1);
    next.splice(dropIdx, 0, moved);
    setItems(next);
    setDragIdx(null);
    persist(next);
  };

  if (items.length === 0) return null;

  return (
    <div className="mb-5 rounded-2xl border border-line bg-white p-5" data-testid={`featured-reorder-${category}`}>
      <div className="flex items-center justify-between">
        <div>
          <div className="font-bold text-ink">{labels.title}</div>
          <p className="mt-0.5 text-sm text-muted-foreground">{labels.blurb}</p>
        </div>
        {saving && <span className="text-xs font-bold text-muted-foreground">Saving…</span>}
      </div>
      <ul className="mt-4 space-y-2">
        {items.map((p, i) => (
          <li
            key={p.id}
            draggable
            onDragStart={() => setDragIdx(i)}
            onDragOver={(e) => e.preventDefault()}
            onDrop={() => onDrop(i)}
            onDragEnd={() => setDragIdx(null)}
            data-testid={`featured-order-item-${p.id}`}
            className={`flex cursor-grab items-center gap-3 rounded-xl border px-3 py-2.5 transition-colors active:cursor-grabbing ${dragIdx === i ? "border-crimson bg-crimson/5" : "border-line bg-white hover:border-ink"}`}
          >
            <GripVertical className="h-4 w-4 shrink-0 text-muted-foreground" />
            <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-ink text-xs font-bold text-white">{i + 1}</span>
            {p.image_url && <img src={p.image_url} alt={p.name} className="h-9 w-9 shrink-0 rounded-lg border border-line object-contain" />}
            <span className="truncate text-sm font-bold text-ink">{p.name}</span>
            <span className="ml-auto shrink-0 text-sm font-bold text-crimson">${p.price}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
